/**
 * UI Scale Utility
 * Manages an app-wide display scale (a "zoom" level) that defaults to 100% and is
 * user-adjustable. The chosen value is persisted per-browser in localStorage and applied to
 * the root <html> element via CSS `zoom`, which scales both text and the pixel-based layout
 * (MUI spacing, drawer widths, paddings) together — reproducing the look of native browser
 * zoom. Kept independent of the theme system so it works before login and across tenant themes.
 *
 * NOTE: the default is 100% because CSS `zoom` at scales < 100% leaves MUI Popover-positioned
 * components (Menu/Select/Autocomplete/Tooltip) mis-anchored — a known interaction between CSS
 * `zoom` and getBoundingClientRect/position:fixed (mui/material-ui#40408, #32129). 90/80/75%
 * remain selectable but carry that limitation; a proper scaling fix is a separate follow-up.
 */

import logger from './logger';

const UI_SCALE_KEY = 'uiScale';

/** Default scale applied when the user has never chosen one. 100% so MUI menus/popovers anchor
 *  correctly out of the box (CSS `zoom` < 100% mis-anchors them — see file header). */
export const DEFAULT_UI_SCALE = 1;

/** Selectable scale options shown in the Display Scale control. */
export const UI_SCALE_OPTIONS = [
  { value: 1, label: '100%' },
  { value: 0.9, label: '90%' },
  { value: 0.8, label: '80%' },
  { value: 0.75, label: '75%' },
];

const ALLOWED_VALUES = UI_SCALE_OPTIONS.map((o) => o.value);

/**
 * Load the saved UI scale from localStorage.
 * Falls back to DEFAULT_UI_SCALE when unset, unparseable, or not one of the allowed options.
 * @returns {number} A valid scale value
 */
export const loadUiScale = () => {
  try {
    const raw = localStorage.getItem(UI_SCALE_KEY);
    if (raw === null) return DEFAULT_UI_SCALE;
    const value = parseFloat(raw);
    return ALLOWED_VALUES.includes(value) ? value : DEFAULT_UI_SCALE;
  } catch (error) {
    logger.error('Failed to load UI scale', { message: error.message });
    return DEFAULT_UI_SCALE;
  }
};

/**
 * Persist the UI scale to localStorage.
 * @param {number} value - Scale value to save
 */
export const saveUiScale = (value) => {
  try {
    localStorage.setItem(UI_SCALE_KEY, String(value));
  } catch (error) {
    logger.error('Failed to save UI scale', { message: error.message });
  }
};

/**
 * Apply the UI scale to the root <html> element so it covers the whole app, including MUI
 * portals (dialogs, menus, tooltips) that mount under <body>.
 * @param {number} value - Scale value to apply
 */
export const applyUiScale = (value) => {
  try {
    document.documentElement.style.zoom = String(value);
  } catch (error) {
    logger.error('Failed to apply UI scale', { message: error.message });
  }
};

export default loadUiScale;
